import { setup } from 'xstate';

import * as Actions from './setup/bag-featured-upsell.machine.actions';
import * as Actors from './setup/bag-featured-upsell.machine.actors';
import {
  type BagFeaturedUpsellContext,
  initialContext,
} from './setup/bag-featured-upsell.machine.context';
import * as Delays from './setup/bag-featured-upsell.machine.delays';
import { type BagFeaturedUpsellEvent } from './setup/bag-featured-upsell.machine.events';
import * as Guards from './setup/bag-featured-upsell.machine.guards';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as BagFeaturedUpsellContext,
    events: {} as BagFeaturedUpsellEvent,
  },
  actions: Actions,
  actors: Actors,
  delays: Delays,
  guards: Guards,
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const bagFeaturedUpsellMachine = model.createMachine({
  id: 'bag-featured-upsell',
  initial: 'waiting-for-bag',
  context: initialContext,
  states: {
    'waiting-for-bag': {
      on: {
        BAG_READY: {
          target: 'checking-eligibility',
          actions: 'assignBag',
        },
      },
    },
    'checking-eligibility': {
      always: [{ guard: 'checkIfEligible', target: 'fetching-product' }, { target: 'skipped' }],
    },
    'fetching-product': {
      invoke: {
        src: 'fetchProduct',
        input: ({ context }) => ({
          lineageKey: context.productLineageKey,
          restaurantId: context.restaurantId ?? '',
          orderChannel: context.orderChannel,
        }),
        onDone: {
          target: 'deciding',
          actions: {
            type: 'assignProduct',
            params: ({ event }) => ({ product: event.output }),
          },
        },
        onError: 'skipped',
      },
    },
    deciding: {
      always: [{ guard: 'checkIfCanOfferProduct', target: 'visible' }, { target: 'skipped' }],
    },
    visible: {
      entry: ['markShown', 'trackView'],
      on: {
        ADD: {
          target: 'adding',
          actions: ['trackAdd', 'addProductToBag'],
        },
        DISMISS: {
          target: 'closing',
          actions: ['trackDismiss', 'markDismissed'],
        },
        CLOSE: {
          target: 'closing',
          actions: 'markDismissed',
        },
      },
    },
    adding: {
      on: {
        ADD_FINISHED: [{ guard: 'checkIfProductAdded', target: 'added' }, { target: 'closing' }],
      },
    },
    added: {
      after: {
        addedDuration: [{ guard: 'checkIfCanAnimate', target: 'animating' }, { target: 'closing' }],
      },
    },
    closing: {
      after: {
        dialogExitDuration: 'closed',
      },
    },
    animating: {
      initial: 'closing-dialog',
      states: {
        'closing-dialog': {
          after: {
            dialogExitDuration: 'dialog-closed',
          },
        },
        'dialog-closed': {},
      },
      on: {
        ANIMATION_FINISHED: 'closed',
      },
    },
    skipped: {
      type: 'final',
    },
    closed: {
      type: 'final',
    },
  },
});
